import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/api';
import {
  FdaDisclaimer, FtcDisclosure, EvidenceBadge, StrengthBadge,
  InteractionBanner, FoodFirstNote,
} from './Disclosures';

// Honest, goal-based supplement recommendations (SS3). The engine is deterministic
// and gated server-side — this just renders what it returns. Willing to say "food
// first" and "save your money"; every rec shows its evidence grade; medication /
// pregnancy flags surface as calm flag-and-defer notes. Used on /you (your goals)
// and /supplements (browse by goal).

export const GOAL_LABELS = {
  sleep: 'Sleep', energy: 'Energy', immunity: 'Immunity', stress: 'Stress',
  muscle: 'Muscle', heart_brain: 'Heart & brain', digestion: 'Digestion',
  bone: 'Bone', general_longevity: 'Longevity',
};

const TIMING_LABELS = {
  morning: 'morning', with_food: 'with food', empty_stomach: 'empty stomach', evening: 'evening',
};

const doseLine = (r) => {
  const parts = [];
  if (r.default_dose != null) {
    parts.push(`${r.default_dose}${r.default_dose_unit ? ' ' + r.default_dose_unit : ''}`);
  }
  if (r.timing_hint && TIMING_LABELS[r.timing_hint]) parts.push(TIMING_LABELS[r.timing_hint]);
  return parts.join(' · ');
};

export const RecItem = ({ r }) => (
  <div className="rounded-xl border border-surface-border dark:border-surface-dark-border p-4 space-y-2">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-base font-semibold text-gray-900 dark:text-white">{r.name}</span>
      <StrengthBadge strength={r.strength} />
      <EvidenceBadge grade={r.evidence_grade} />
    </div>
    {r.rationale && (
      <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{r.rationale}</p>
    )}
    <FoodFirstNote note={r.food_first_note} />
    {(doseLine(r) || r.price_hint) && (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {doseLine(r) && <>Typical: {doseLine(r)}</>}
        {doseLine(r) && r.price_hint && ' · '}
        {r.price_hint && <>~{r.price_hint}</>}
      </p>
    )}
    <InteractionBanner reasons={r.gate?.reasons} />
    {/* Affiliate surface (SS4) — renders only when a partner link is present;
        nullable today, so nothing shows. The engine never lets a link change a
        rec's strength or grade. */}
    {r.affiliate_url && (
      <a
        href={r.affiliate_url}
        target="_blank"
        rel="nofollow sponsored noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full border border-primary-500/50 text-primary-700 dark:text-primary-300 hover:bg-primary-500/10 transition-colors"
      >
        Where to buy →
      </a>
    )}
  </div>
);

const RecommendationsCard = ({ title = 'Supplement suggestions', showGoalPicker = true }) => {
  const [allGoals, setAllGoals] = useState([]);
  const [selected, setSelected] = useState([]);      // empty → server derives from profile
  const [recs, setRecs] = useState(null);
  const [activeGoals, setActiveGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/api/recommendations/goals')
      .then((res) => setAllGoals(res.data?.goals || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selected.length ? { goals: selected.join(',') } : {};
      const res = await apiClient.get('/api/recommendations', { params });
      setRecs(res.data?.recommendations || []);
      setActiveGoals(res.data?.goals || []);
    } catch {
      setRecs([]);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const toggleGoal = (g) =>
    setSelected((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));

  const hasAffiliate = (recs || []).some((r) => r.affiliate_url);

  return (
    <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Evidence-graded, and honest about when food does the job or you can skip it.
        </p>
      </div>

      {showGoalPicker && allGoals.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allGoals.map((g) => {
            const on = selected.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGoal(g)}
                className={`text-sm px-2.5 py-1 rounded-full border transition-colors ${
                  on
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'border-surface-border dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-raised/50'
                }`}
              >
                {GOAL_LABELS[g] || g}
              </button>
            );
          })}
        </div>
      )}

      {!showGoalPicker && activeGoals.length > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          For your goal: {activeGoals.map((g) => GOAL_LABELS[g] || g).join(', ')}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (recs || []).length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Nothing to suggest here — and that’s often the honest answer. Food and
          habits carry most of the weight.
        </p>
      ) : (
        <div className="space-y-3">
          {recs.map((r, i) => <RecItem key={`${r.goal}-${r.slug}-${i}`} r={r} />)}
        </div>
      )}

      <div className="pt-2 border-t border-surface-border dark:border-surface-dark-border space-y-2">
        <FtcDisclosure hasAffiliate={hasAffiliate} />
        <FdaDisclaimer />
      </div>
    </div>
  );
};

export default RecommendationsCard;
